import { useContext, useState } from 'react';
import { Alert, Button, Card, Container, Form, Spinner, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import '../styles/LoginPage.css';

const BASE_URL = process.env.REACT_APP_API_URL;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, userData } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    email: '',
    password: ''
    , company_code: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(null);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [modalEmail, setModalEmail] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/api/admin/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        // Save user data along with token
        const userData = {
          id: data.user.id,
          company_code: data.user.company_code,
          name: data.user.name,
          phone_number: data.user.phone_number,
          email: data.user.email,
          role: data.user.role,
          side_bar_options: data.user.side_bar_options || [],
          company_logo: data.user.company_logo || null
        };
        login(data.token, userData);
        const role = data.user.role;

        // Navigate based on role - map role to dashboard path
        const dashboardMap = {
          VCM_Admin: '/vcm-admin-dashboard',
          Company_Admin: '/dashboard',
        };
        if (dashboardMap[role]) {
          navigate(dashboardMap[role]);
        } else if (role && role.toLowerCase().includes('dashboard')) {
          // If role is a dashboard path, use it directly
          navigate(`/${role}`);
        } else if (role && role !== 'VCM_Admin' && role !== 'Company_Admin') {
          navigate(`/dashboard`);
        } else {
          setErrorMsg('Unknown user role. Please contact administrator.');
        }
      } else {
        setErrorMsg(data.message || "Login failed");
      }
    } catch (error) {
      console.error("Error during login:", error);
      setErrorMsg("Invalid Password.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleForgotPassword = async (emailParam) => {
    setForgotMsg('');
    setForgotSuccess(null);
    setForgotLoading(true);
    try {
      const emailToUse = emailParam || (userData && userData.email) || formData.email;
      if (!emailToUse) {
        setForgotMsg('No email available.');
        setForgotLoading(false);
        setForgotSuccess(false);
        return;
      }

      // Send password reset request
      const res = await fetch(`${BASE_URL}/api/admin/auth/admin-request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailToUse,
          frontend_url: window.location.origin
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setForgotMsg('Password reset email sent to the registered email address.');
        setForgotSuccess(true);
      } else {
        setForgotMsg(data.message || 'Failed to send reset email.');
        setForgotSuccess(false);
      }
    } catch (err) {
      console.error('Forgot password error', err);
      setForgotMsg('Something went wrong while requesting password reset.');
      setForgotSuccess(false);
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="login-container">
      <Container>
        <Card className="login-card mx-auto">
          <Card.Body>
            <h1 className="login-title">Welcome Back!</h1>

            {errorMsg && (
              <Alert variant="danger" className="text-center">
                {errorMsg}
              </Alert>
            )}

            <Form className="login-form" onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="email">Email Address</Form.Label>
                <Form.Control
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Enter your email"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Label htmlFor="password" className="mb-0">Password</Form.Label>
                  <small>
                    <button
                      type="button"
                      className="forgot-pwd btn btn-link p-0"
                      onClick={() => {
                        // Prefill modal email from known sources
                        const prefill = (userData && userData.email) ? userData.email : formData.email || '';
                        setModalEmail(prefill);
                        setForgotMsg('');
                        setShowForgotModal(true);
                      }}
                      disabled={forgotLoading}
                    >
                      {forgotLoading ? 'Sending...' : 'Forgot password?'}
                    </button>
                  </small>
                </div>

                <div className="password-wrapper">
                  <Form.Control
                    className="password-input"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Enter your password"
                  />

                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(prev => !prev)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </Form.Group>

              <Button
                type="submit"
                className="login-btn w-100"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </Container>
      <Modal show={showForgotModal} onHide={() => setShowForgotModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Forgot password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Enter email address</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={modalEmail}
                onChange={(e) => setModalEmail(e.target.value)}
                placeholder="Enter your registered email"
              />
            </Form.Group>

            {forgotMsg && (
              <Alert variant={forgotSuccess ? 'success' : 'danger'}>
                {forgotMsg}
              </Alert>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForgotModal(false)} disabled={forgotLoading}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              await handleForgotPassword(modalEmail.trim());
            }}
            disabled={forgotLoading}
          >
            {forgotLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Sending...
              </>
            ) : (
              'Send Reset Link'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}



